import { toggleCategory } from '../utils'
import { parseAnswer } from '../utils/answerParser'
import { sendRequest, getConfig } from '../api/sendRequest'
import DOMPurify from 'dompurify'

export default (state) => ({
    /**
   * Toggles the category and resets the faqs
   * @param {*} category
   */
    selectCategory (category, toggle = true) {
        // Always make a fresh copy of selected categories
        let newSelectedCategories = [...state.selectedCategories]

        if (toggle) {
            // Use toggleCategory for normal navigation
            newSelectedCategories = toggleCategory(category, newSelectedCategories)
        } else {
            // For breadcrumb navigation, remove categories when navigating back in depth
            newSelectedCategories = newSelectedCategories.slice(0, category.depth + 1)
            newSelectedCategories[category.depth] = category.id

            // If this category has subcategories, add a placeholder for next level
            if (category.subCategories && category.subCategories.length > 0) {
                newSelectedCategories.push(-1)
            }
        }

        // Update state
        state.selectedCategories = newSelectedCategories
        state.depth = category.depth

        // Reset and update FAQs
        this.resetSelectedFaqs()
        if (category.items && category.items.length > 0) {
            state.faqs = category.items
        } else {
            state.faqs = []
        }
    },

    /**
    * Gets all categories
    */
    async getCategoryTree () {
        try {
            const payload = {
                data: {
                    classificationId: state.classificationId
                },
                metadata: {
                    includeFaqs: true
                },
                type: 'categorytree'
            }
            const response = await sendRequest(state.configId, payload)

            state.sessionId = response.metadata.sessionId
            state.categories = response.data.result
        } catch (error) {
            console.error(error)
        }
    },
    /**
     * Gets the specific config
     * @param {String} configId
     * @param {boolean} isPreviewMode Preview mode doesn't use cache
     */
    async getConfig (configId, isPreviewMode) {
        const faqConfig = await getConfig(configId, isPreviewMode)

        state.configId = configId

        return faqConfig.config
    },
    /**
   * Resets the faqs
   */
    resetSelectedFaqs () {
        state.faqs = state.faqs.map((faq) => (faq.isSelected = false))
    },
    /**
     * Gets the faq answer
     * @param {Number} id of the faq
     * @param {String} answer of the faq if defined
     * @param {String} faq question
     */
    async getFaqAnswer ({ id, answer }) {
        const selectedFaqIndex = state.faqs.findIndex((faq) => (faq.id === id))

        if (selectedFaqIndex !== -1) { state.faqs[selectedFaqIndex].isSelected = !state.faqs[selectedFaqIndex].isSelected }

        if (!answer && id) {
            // to avoid double request
            if (
                !state.faqs.some(
                    (faq) => faq.id === id && faq.isSelected && typeof answer !== 'undefined'
                )
            ) {
                try {
                    const faqPayload = {
                        data: {
                            faqId: id
                        },
                        metadata: {
                            sessionId: state.sessionId,
                            context: state.context
                        },
                        type: 'faq'
                    }

                    const response = await sendRequest(state.configId, faqPayload)

                    const faqAnswer = parseAnswer(response)

                    let currentFaq = state.faqs.find(faq => faq.id === faqAnswer.metadata.id)

                    if (currentFaq) {
                        currentFaq.answer = faqAnswer.data.answer
                        currentFaq.feedback = {
                            interactionId: faqAnswer.metadata.interactionId,
                            shouldShowTextarea: false,
                            message: state.feedback.intro
                        }
                    } else {
                        currentFaq = state.faqs.find(faq => faq.id === id)
                        currentFaq.answer = undefined
                    }

                    return currentFaq
                } catch (error) {
                    console.error(error)
                }
            }
        }
    },
    /**
   * search FAQs
   */
    async searchFaq (userInput) {
        userInput = DOMPurify.sanitize(userInput)

        const searchPayload = {
            data: {
                classificationId: state.classificationId,
                userInput
            },
            metadata: {},
            type: 'searchfaqs'
        }

        try {
            const response = await sendRequest(state.configId, searchPayload)

            state.searchResults = response.data.result
        } catch (error) {
            console.error(error)
        }
    },
    async selectSearchResult ({ id, answer }) {
        if (!answer && id) {
            try {
                const payload = {
                    data: {
                        faqId: id
                    },
                    metadata: {
                        sessionId: state.sessionId,
                        context: state.context
                    },
                    type: 'faq'
                }

                const response = await sendRequest(state.configId, payload)

                const faqAnswer = parseAnswer(response)

                state.searchAnswer = faqAnswer
            } catch (error) {
                console.error(error)
            }
        }
    },
    /**
 * Resets the search answer
 */
    resetSearchAnswer () {
        state.searchAnswer = []
    },
    /**
     * Adds width values to views to enable Container Queries
     * @param {Array} width Array with strings used in CSS selectors
     */
    setWidth (width) {
        state.width = width
    },
    /**
     * Defines how many category nodes can be show in the view container
     * @param {Number} nodesInView Amount of columns
     */
    setNodesInView (nodesInView) {
        state.nodesInView = nodesInView
    },
    /**
     * Callback fn triggered on Observed resize events and initial load
     * @param {HTMLElement} elm container to set scroll position
     * @param {Object} actions
     */
    resize (elm, actions) {
        let fontSize = window.getComputedStyle(
            document.documentElement,
            null
        ).fontSize

        fontSize = parseFloat(fontSize) || 16
        const appWidth = Math.ceil(elm.offsetWidth / fontSize)
        const minWidthArr = []

        // maximum amount of breakpoints.
        const max = state.maxBreakpoints || 3
        // amount of size increment in each "breakpoint".
        const step = 20

        for (let i = 1; i <= max; i++) {
            const val = step * i

            if (appWidth >= val) {
                minWidthArr.push(`${val}rem`)
            }
        }
        actions.setWidth(minWidthArr.join(' '))
        actions.setNodesInView(minWidthArr.length)

        elm.scrollLeft = elm.scrollWidth
    },
    /**
      * Send Feedback or Show text area on negative feedback.
      * If score is negative but there is no comment then show the textarea.
      * Negative feedback is submitted once comment isn't empty.
      * @param {Object} clickedFaq
      * @param {Number} feedbackScore
      */
    feedbackClick (clickedFaq, feedbackScore) {
        const { id, feedback } = clickedFaq
        const isPositiveFeedback = feedbackScore > 0
        const { score = feedbackScore, interactionId, comment, label = isPositiveFeedback ? state.feedback.positive : state.feedback.negative } = feedback
        const hasComment = !!comment

        state.faqs = state.faqs.map((faq) => {
            if (faq.id === id) {
                faq.feedback.score = score
                faq.feedback.label = label
                faq.feedback.shouldShowTextarea = !hasComment && !isPositiveFeedback

                if (hasComment || isPositiveFeedback) {
                    faq.feedback.message = state.feedback.thanks
                    faq.feedback.sent = true
                    faq.feedback.showFeedbackNotify = true
                }
            }

            return faq
        })

        if (hasComment || isPositiveFeedback) {
            this.sendFeedback({
                score: feedbackScore,
                interactionId,
                comment: isPositiveFeedback ? undefined : comment,
                label: isPositiveFeedback ? state.feedback.positive : state.feedback.negative
            })
        }
    },
    /**
     * Updates the comment of the specific faq
     * @param {Number} faqId
     * @param {String} input
     */
    updateFaqFeedbackComment (faqId, input) {
        state.faqs = state.faqs.map((faq) => {
            if (faq.id === faqId) {
                faq.feedback.comment = DOMPurify.sanitize(input.target.value)
            }

            return faq
        })
    },
    /**
     * Sends the feedback
     * @param {Object} feedbackPayload
     */
    async sendFeedback (feedbackPayload) {
        try {
            const payload = {
                data: { ...feedbackPayload },
                metadata: {
                    sessionId: state.sessionId
                },
                type: 'feedback'
            }

            await sendRequest(state.configId, payload)
        } catch (error) {
            console.error(error)
        }
    },
    /**
     * Toggles the feedback show notify of specific faq
     * @param {Number} faqId
     */
    toggleFeedbackNotify (faqId) {
        state.faqs = state.faqs.map((faq) => {
            if (faq.id === faqId) {
                faq.feedback.showFeedbackNotify = !faq.feedback.showFeedbackNotify
            }

            return faq
        })
    }
})
